export const TIERS = [
  { key: "big_win", code: "BW", label: "Big Win", value: 2, bg: "#064E3B", text: "#6EE7B7", border: "#059669" },
  { key: "little_win", code: "LW", label: "Little Win", value: 1, bg: "#14532D", text: "#86EFAC", border: "#16A34A" },
  { key: "little_loss", code: "LL", label: "Little Loss", value: -1, bg: "#7F1D1D", text: "#FCA5A5", border: "#DC2626" },
  { key: "big_loss", code: "BL", label: "Big Loss", value: -2, bg: "#450A0A", text: "#FECACA", border: "#991B1B" },
];

export const TIER_BY_KEY = Object.fromEntries(TIERS.map((t) => [t.key, t]));

export const MODIFIERS = [
  { key: "plus_plus", label: "++", value: 0.5, color: "#22d3ee", hint: "Strongly favoured" },
  { key: "plus", label: "+", value: 0.25, color: "#38BDF8", hint: "Favoured" },
  { key: "neutral", label: "•", value: 0, color: "#94A3B8", hint: "Neutral" },
  { key: "minus", label: "−", value: -0.25, color: "#F43F5E", hint: "Unfavoured" },
  { key: "minus_minus", label: "−−", value: -0.5, color: "#fb7185", hint: "Strongly unfavoured" },
];

export const MOD_BY_KEY = Object.fromEntries(MODIFIERS.map((m) => [m.key, m]));

export const cellValue = (cell) => {
  if (!cell || !cell.tier) return 0;
  const t = TIER_BY_KEY[cell.tier];
  const m = MOD_BY_KEY[cell.modifier || "neutral"];
  return (t ? t.value : 0) + (m ? m.value : 0);
};

export const cellLabel = (cell) => {
  if (!cell || !cell.tier) return "";
  const t = TIER_BY_KEY[cell.tier];
  const m = MOD_BY_KEY[cell.modifier];
  const mod = cell.modifier && cell.modifier !== "neutral" && m ? m.label : "";
  return `${t.code}${mod}`;
};

// Current Warmachine (MkIV) armies, matched to Longshanks faction icons.
export const ARMIES = [
  { name: "Storm Legion", base: "storm_legion" },
  { name: "Winter Korps", base: "winter_korps" },
  { name: "Sea Raiders", base: "orgoth" },
  { name: "Brineblood Marauders", base: "brineblood_marauders" },
  { name: "House Kallyss", base: "house_kallyss" },
  { name: "Shadowflame Shard", base: "shadowflame_shard" },
  { name: "Kithguard", base: "kithguard" },
  { name: "Fane of Nyrro", base: "fane_nyrro" },
  { name: "Old Umbrey", base: "old_umbrey" },
  { name: "Necrofactorium", base: "necrofactorium" },
  { name: "Gravediggers", base: "gravediggers" },
  { name: "Dark Operations", base: "dark_operations" },
  { name: "Wicked Harvest", base: "grymkin" },
  { name: "Clockwork Legions", base: "cyriss" },
  { name: "Crucible Guard", base: "crucible_guard" },
  { name: "Shadows of the Retribution", base: "scyrah", abbr: "SR" },
  { name: "Final Interdiction", base: "menoth", abbr: "FI" },
  { name: "Army of the Western Reaches", base: "skorne", abbr: "AWR" },
  { name: "Rhul Guard", base: "mercenaries", abbr: "RG" },
  { name: "Soldiers of Fortune", base: "mercenaries", abbr: "SF" },
  { name: "Thornfall Alliance", base: "mercenaries", abbr: "TA" },
];

export const ARMY_BY_NAME = Object.fromEntries(ARMIES.map((a) => [a.name, a]));

export const factionIcon = (name) => {
  const a = ARMY_BY_NAME[name];
  return a ? `https://www.longshanks.org/systems/warmachine/factions/${a.base}.png` : null;
};

export const fmtScore = (v) => (v > 0 ? `+${v}` : `${v}`);

// Win count is what wins a WTC round: the engine optimises a win-weighted objective
// (each win ≈ ±WIN_WEIGHT). Divide back out to read the projected net-win delta.
export const WIN_WEIGHT = 100;
export const objWinDelta = (obj) => Math.round((obj || 0) / WIN_WEIGHT);

// "3× LW · 2× LL" from a {big_win,little_win,little_loss,big_loss} breakdown.
export const breakdownLabel = (bd) => {
  if (!bd) return "";
  return ["big_win", "little_win", "little_loss", "big_loss"]
    .filter((k) => bd[k])
    .map((k) => `${bd[k]}× ${TIER_BY_KEY[k].code}`)
    .join(" · ");
};

// Every unrated matchup defaults to Little Win (+1): users only change exceptions.
export const DEFAULT_CELL = { tier: "little_win", modifier: "neutral" };

// Visual style (bg/text/border) for a cell's tier — used to colour rows/options.
export const tierStyle = (cell) => {
  const t = cell?.tier ? TIER_BY_KEY[cell.tier] : TIER_BY_KEY.little_win;
  return { bg: t.bg, text: t.text, border: t.border };
};

// Style from a raw projected value (fractional sums rounded to the nearest tier).
export const valueStyle = (v) => {
  const key = v >= 1.5 ? "big_win" : v > 0 ? "little_win" : v > -1.5 ? "little_loss" : "big_loss";
  const t = TIER_BY_KEY[key];
  return { bg: t.bg, text: t.text, border: t.border };
};

// Resolve the effective rating for (yourPlayer vs oppPlayer): per-cell override
// first, else the player's faction default, else the Little-Win default.
export const effectiveCell = (setup, yourId, oppId, oppFaction) => {
  const override = setup.matrix?.[`${yourId}|${oppId}`];
  if (override && override.tier) return { ...override, source: "override" };
  const def = setup.faction_ratings?.[yourId]?.[oppFaction];
  if (def && def.tier) return { ...def, source: "default" };
  return { ...DEFAULT_CELL, source: "fallback" };
};
